import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight, Mail, MapPin, Phone, Globe } from "lucide-react";
import { SiLinkedin, SiX } from "react-icons/si";
import { ROUTE_PATHS } from "@/lib/index";
import { Button } from "@/components/ui/button";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { name: "Inicio", path: ROUTE_PATHS.HOME },
    { name: "Servicios", path: ROUTE_PATHS.SERVICES },
    { name: "AI Agents", path: ROUTE_PATHS.AGENTS },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground font-sans">
      {/* Header */}
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled
            ? "bg-background/80 backdrop-blur-md border-b border-border py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group">
            <img 
              src="/images/pryma_isotipo_opcion_2_fondo_transparente_1.png" 
              alt="Pryma Digital Logo" 
              className="h-10 w-auto transition-transform group-hover:scale-110 group-hover:rotate-12"
            />
            <span className="text-xl font-bold tracking-tight">
              PRYMA <span className="bg-gradient-to-r from-primary to-yellow-400 bg-clip-text text-transparent">DIGITAL</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:text-primary ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-none px-6">
              <Link to={ROUTE_PATHS.CONTACT}>Contacto</Link>
            </Button>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-40 bg-background flex flex-col pt-24 px-8"
          >
            <nav className="flex flex-col gap-8">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    `text-3xl font-bold transition-colors ${
                      isActive ? "text-primary" : "text-foreground"
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              ))}
              <Link
                to={ROUTE_PATHS.CONTACT}
                className="text-3xl font-bold text-primary flex items-center gap-2 underline underline-offset-8"
              >
                Contacto <ChevronRight size={28} />
              </Link>
            </nav>

            <div className="mt-auto mb-12 flex flex-col gap-4">
              <div className="flex gap-6">
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                  <SiLinkedin size={24} />
                </a>
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                  <SiX size={24} />
                </a>
              </div>
              <p className="text-sm text-muted-foreground">
                Transformando negocios a través de la Inteligencia Artificial.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-black text-white pt-20 pb-10 border-t border-white/10">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3">
                <img 
                  src="/images/pryma_isotipo_opcion_2_fondo_transparente_1.png" 
                  alt="Pryma Digital Logo" 
                  className="h-8 w-auto"
                />
                <span className="text-xl font-bold tracking-tight text-white">
                  PRYMA <span className="bg-gradient-to-r from-primary to-yellow-400 bg-clip-text text-transparent">DIGITAL</span>
                </span>
              </Link>
              <p className="text-gray-400 leading-relaxed">
                Lideramos la frontera de la IA y la automatización inteligente para redefinir el futuro de las operaciones empresariales.
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-yellow-400 hover:text-black transition-all">
                  <SiLinkedin size={18} />
                </a>
                <a href="#" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-yellow-400 hover:text-black transition-all">
                  <SiX size={18} />
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-6 text-white uppercase tracking-wider">Navegación</h3>
              <ul className="space-y-4">
                <li><Link to={ROUTE_PATHS.AGENTS} className="text-gray-400 hover:text-yellow-400 transition-colors">AI Agents</Link></li>
                <li><Link to={ROUTE_PATHS.SERVICES} className="text-gray-400 hover:text-yellow-400 transition-colors">Servicios</Link></li>
                <li><Link to={ROUTE_PATHS.CONTACT} className="text-gray-400 hover:text-yellow-400 transition-colors">Contacto</Link></li>
              </ul>
            </div>

            <div className="lg:ml-auto lg:max-w-xs">
              <h3 className="text-lg font-bold mb-6 text-white uppercase tracking-wider">Contacto</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 hover:text-yellow-400 transition-colors cursor-pointer">
                  <MapPin size={20} className="text-yellow-400 shrink-0" />
                  <span className="text-gray-400 group-hover:text-yellow-400">Badajoz 100, oficina 1014, piso 10<br />Las Condes, Santiago, Chile</span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={20} className="text-yellow-400 shrink-0" />
                  <a href="mailto:hola@prymadigital.com" className="text-gray-400 hover:text-yellow-400 transition-colors">hola@prymadigital.com</a>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={20} className="text-yellow-400 shrink-0" />
                  <a href="https://wa.me/56949764444" className="text-gray-400 hover:text-yellow-400 transition-colors">+56 9 4976 4444</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-gray-500 text-sm">
              © 2026 Pryma Digital. Todos los derechos reservados.
            </p>
            <div className="flex gap-8">
              <Link to="#" className="text-gray-500 hover:text-yellow-400 text-xs uppercase tracking-widest transition-colors">Privacidad</Link>
              <Link to="#" className="text-gray-500 hover:text-yellow-400 text-xs uppercase tracking-widest transition-colors">Términos</Link>
              <Link to="#" className="text-gray-500 hover:text-yellow-400 text-xs uppercase tracking-widest transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
